import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { GlobalSupplier, GlobalSupplierRow } from 'src/app/models/suppliers/global-supplier.model';
import { IS_OVERVIEW_APP, IS_SELFNET_APP } from 'src/app/models/users/users.model';
import { CognitoService } from '../cognito/cognito.service';
import { ConfigurationService } from '../configuration.service';
import { LocalStorageKeys } from '../local-storage-keys';
import { AlertService, MessageSeverity } from '../utilities/alert.service';
import { GlobalSupplierEndpoint } from './global-supplier.endpoint';
import { GLOBAL_SUPPLIER_PAGE_APPS } from './global-supplier.pages';

/**
 * Holds the global supplier selected in the navbar, which drives the pages of Overview and Selfnet.
 * No supplier selected means no filter.
 */
@Injectable({
  providedIn: 'root',
})
export class GlobalSupplierService {
  public static readonly LANDING_PAGE = '/pages/dashboard';
  public static readonly SELFNET_LANDING_PAGE = '/pages/research';

  public readonly current = new BehaviorSubject<GlobalSupplierRow>(null);
  public rows: GlobalSupplierRow[] = [];
  public isAvailable: boolean = true;

  private storageUser: string = null;

  constructor(
    private globalSupplierEndpoint: GlobalSupplierEndpoint,
    private cognitoService: CognitoService,
    private configurations: ConfigurationService,
    private alertService: AlertService,
    private router: Router
  ) {
    this.cognitoService.user.subscribe((user) => {
      if (user?.username && user.username !== this.storageUser) {
        this.storageUser = user.username;
        this.current.next(this.readFromStorage());
      }
    });
  }

  get value(): GlobalSupplierRow {
    return this.current.getValue();
  }

  /**
   * Load the suppliers of Overview and Selfnet and check that the selected one still exists
   */
  public async loadSuppliers(): Promise<GlobalSupplierRow[]> {
    try {
      const suppliers = await this.globalSupplierEndpoint.getGlobalSuppliers().toPromise();
      this.rows = (suppliers ?? []).map((supplier) => GlobalSupplierService.toRow(supplier));
      this.isAvailable = true;
      this.checkSelectedSupplier();
    } catch (error) {
      if (error?.status === 403) {
        this.isAvailable = false;
        this.setSupplier(null);
      } else {
        this.alertService.showStickyMessage('Errore', 'Impossibile caricare la lista delle reti', MessageSeverity.error, error);
      }
    }

    return this.rows;
  }

  /**
   * Select a supplier (null for all suppliers), then reload the current page,
   * or leave it when its application is not enabled for the new supplier
   */
  public changeSupplier(row: GlobalSupplierRow) {
    if ((row?.key ?? null) === (this.value?.key ?? null)) {
      return;
    }

    this.setSupplier(row);

    if (this.cognitoService.isExternalPortal) {
      return;
    }

    if (!this.isPageEnabled(this.pagePath(this.router.url))) {
      this.router.navigateByUrl(this.landingPage());
      return;
    }

    this.reloadCurrentPage();
  }

  /**
   * Supplier ids of the given application to filter on: null means no filter,
   * an empty list means that the selected supplier does not exist on that application
   */
  public getSupplierIds(app: string): number[] | null {
    if (this.cognitoService.isExternalPortal) {
      return app === IS_OVERVIEW_APP && this.configurations.localSupplierId != null ? [Number(this.configurations.localSupplierId)] : null;
    }

    const row = this.value;
    if (row == null) {
      return null;
    }

    const supplier = app === IS_OVERVIEW_APP ? row.overview : app === IS_SELFNET_APP ? row.selfnet : null;
    return supplier ? [supplier.id] : [];
  }

  /**
   * Name of the selected supplier on the given application: null without a selection, or when the supplier
   * does not exist on that application
   */
  public getSupplierName(app: string): string | null {
    const row = this.cognitoService.isExternalPortal ? null : this.value;
    const supplier = app === IS_OVERVIEW_APP ? row?.overview : app === IS_SELFNET_APP ? row?.selfnet : null;
    return supplier?.name ?? null;
  }

  /**
   * Keep the rows of the selected supplier of the given application, or all of them without a selection
   * @param rows rows with the supplier id of the application
   * @param app application of the supplier id: 'ov' or 'sn'
   */
  public filterRows<T extends { supplier_id?: number }>(rows: T[], app: string): T[] {
    const supplierIds = this.getSupplierIds(app);
    return supplierIds == null ? rows : rows.filter((row) => supplierIds.includes(row.supplier_id));
  }

  /**
   * Return true when the application is enabled for the selected supplier
   */
  public isAppEnabled(app: string): boolean {
    const row = this.value;
    if (row == null || app == null) {
      return true;
    }

    return app === IS_OVERVIEW_APP ? row.overview != null : row.selfnet != null;
  }

  /**
   * Return true when the page (route path under /pages) is enabled for the selected supplier
   */
  public isPageEnabled(path: string): boolean {
    return this.isAppEnabled(GLOBAL_SUPPLIER_PAGE_APPS[path] ?? null);
  }

  /**
   * Return the page to open when the current one is not enabled: Ricerca for a Selfnet only supplier
   */
  public landingPage(): string {
    return this.isAppEnabled(IS_OVERVIEW_APP) ? GlobalSupplierService.LANDING_PAGE : GlobalSupplierService.SELFNET_LANDING_PAGE;
  }

  /**
   * Return the route path under /pages of an url
   */
  public pagePath(url: string): string {
    const segments = url.split(/[?#]/)[0].split('/').filter((segment) => segment.length > 0);
    return segments.length > 1 ? segments[1] : null;
  }

  /**
   * Return the text of a supplier: the names separated by " | ", then the code
   */
  public static description(row: GlobalSupplierRow): string {
    if (row == null) {
      return '';
    }

    const names = [row.overview?.name, row.selfnet?.name].filter((name) => name != null && name.length > 0).join(' | ');
    return row.code ? `${names} - ${row.code}` : names;
  }

  private static toRow(supplier: GlobalSupplier): GlobalSupplierRow {
    return {
      ...supplier,
      key: `${supplier.code}|${supplier.overview?.id ?? ''}|${supplier.selfnet?.id ?? ''}`,
      overview_name: supplier.overview?.name ?? '',
      selfnet_name: supplier.selfnet?.name ?? '',
    };
  }

  private checkSelectedSupplier() {
    const row = this.value;
    if (row == null) {
      return;
    }

    const found = this.rows.find((r) => r.key === row.key) ?? this.findSameSupplier(row);
    if (found == null) {
      this.changeSupplier(null);
    } else if (found.overview?.id === row.overview?.id && found.selfnet?.id === row.selfnet?.id) {
      this.setSupplier(found);
    } else {
      this.changeSupplier(found);
    }
  }

  /**
   * Find the row of the same Overview supplier, or else of the same Selfnet supplier,
   * when the code or the link between the two has changed
   */
  private findSameSupplier(row: GlobalSupplierRow): GlobalSupplierRow {
    return this.rows.find((r) => row.overview != null && r.overview?.id === row.overview.id)
      ?? this.rows.find((r) => row.selfnet != null && r.selfnet?.id === row.selfnet.id);
  }

  private setSupplier(row: GlobalSupplierRow) {
    const key = this.storageKey();
    if (key != null) {
      if (row == null) {
        localStorage.removeItem(key);
      } else {
        localStorage.setItem(key, JSON.stringify(row));
      }
    }

    this.current.next(row);
  }

  private readFromStorage(): GlobalSupplierRow {
    const key = this.storageKey();
    const stored = key != null ? localStorage.getItem(key) : null;
    if (stored == null) {
      return null;
    }

    try {
      return JSON.parse(stored) as GlobalSupplierRow;
    } catch {
      localStorage.removeItem(key);
      return null;
    }
  }

  private storageKey(): string {
    return this.storageUser != null ? `${LocalStorageKeys.GLOBAL_SUPPLIER}_${this.storageUser}` : null;
  }

  /**
   * Recreate the component of the current page, keeping the layout
   */
  private reloadCurrentPage() {
    const strategy = this.router.routeReuseStrategy;
    const shouldReuseRoute = strategy.shouldReuseRoute;
    const onSameUrlNavigation = this.router.onSameUrlNavigation;

    strategy.shouldReuseRoute = (future, curr) => future.firstChild != null && shouldReuseRoute.call(strategy, future, curr);
    this.router.onSameUrlNavigation = 'reload';

    this.router.navigateByUrl(this.router.url).finally(() => {
      strategy.shouldReuseRoute = shouldReuseRoute;
      this.router.onSameUrlNavigation = onSameUrlNavigation;
    });
  }
}
